/* PatientChat — 1:1 thread between the clinic and each of its patients.
 *
 * Mirrors the Lab Chat UX (two-pane: inbox left, thread right) but
 * scoped per PATIENT, not per case. A patient has ONE thread with the
 * clinic regardless of how many cases they have.
 *
 * URL: /doctor/patient-chat?patient=<patientId>
 * Cross-link from Patient Profile (Overview tab) opens a thread directly.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
    ChatCircleDots, PaperPlaneTilt, MagnifyingGlass, ArrowRight, ArrowLeft, Spinner, ArrowsClockwise,
    Paperclip, X, FileText, Image as ImageIcon, Check, Checks,
    Sparkle, ClockCountdown, Smiley, ChatTeardropDots, Warning, GraduationCap,
    Note, User, CaretRight, Notebook, DotsThreeVertical, Lock, Plus,
} from "@phosphor-icons/react";


// Deterministic tint for the initials avatar — keeps the same patient
// the same color across mounts so the doctor builds a visual memory.
const AVATAR_TINTS = [
    { bg: "bg-primary/15", border: "border-primary/30", text: "text-primary" },
    { bg: "bg-indigo-50",  border: "border-indigo-200", text: "text-indigo-700" },
    { bg: "bg-amber-50",   border: "border-amber-200", text: "text-amber-800" },
    { bg: "bg-rose-50",    border: "border-rose-200", text: "text-rose-700" },
    { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700" },
    { bg: "bg-sky-50",     border: "border-sky-200", text: "text-sky-700" },
];

function avatarTintFor(name) {
    const s = String(name || "?");
    let h = 0;
    for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) | 0;
    return AVATAR_TINTS[Math.abs(h) % AVATAR_TINTS.length];
}

const initialsOf = (name) =>
    String(name || "?")
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((s) => s[0].toUpperCase())
        .join("") || "?";


const POLL_MS = 30_000;
const FAST_POLL_MS = 5_000;


/* Icon picker for template chips. Server stores `category` only; we
   pick a sensible icon per category here. Falls back to Note for any
   category we don't have a glyph for yet. */
const TEMPLATE_CATEGORY_ICON = {
    welcome:   Sparkle,
    checkin:   Smiley,
    education: GraduationCap,
    followup:  ChatTeardropDots,
    billing:   ClockCountdown,
    other:     Note,
};


function fmtTime(iso) {
    if (!iso) return "";
    const t = new Date(iso);
    if (Number.isNaN(t.getTime())) return "";
    const now = new Date();
    const sameDay = t.toDateString() === now.toDateString();
    if (sameDay) return t.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
    const within7d = now.getTime() - t.getTime() < 7 * 86400000;
    if (within7d) return t.toLocaleString([], { weekday: "short", hour: "2-digit", minute: "2-digit", hour12: false });
    return t.toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", hour12: false });
}


function fmtSize(bytes) {
    if (!bytes || Number.isNaN(bytes)) return "—";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}


export default function PatientChat() {
    const [searchParams, setSearchParams] = useSearchParams();
    const activePatientId = searchParams.get("patient") || null;

    const [inbox, setInbox] = useState([]);
    const [inboxLoading, setInboxLoading] = useState(true);
    const [inboxQ, setInboxQ] = useState("");
    /* iter98.36 — Full doctor roster for the inbox search. Lets the
       doctor *search* for any of their patients (not just ones who
       already have a thread) and start a fresh conversation. The list
       is fetched once on mount + refreshed on inbox reload so brand-new
       patients added during the session also show up. */
    const [roster, setRoster] = useState([]);
    const [thread, setThread] = useState([]);
    const [threadLoading, setThreadLoading] = useState(false);
    const [draft, setDraft] = useState("");
    const [pendingAttachments, setPendingAttachments] = useState([]);
    const [uploadingCount, setUploadingCount] = useState(0);
    const [sending, setSending] = useState(false);
    const [trayOpen, setTrayOpen] = useState(false);
    /* Per-clinic templates fetched from /api/doctor/chat-templates. On
       first load, if the clinic has zero templates we POST to
       /seed-defaults so they immediately see the 6 starter messages —
       same content as the old hardcoded constant. */
    const [templates, setTemplates] = useState([]);
    const scrollRef = useRef(null);
    const pollTimerRef = useRef(null);
    const fileInputRef = useRef(null);

    const reloadInbox = useCallback(async () => {
        try {
            const { data } = await api.get("/doctor/patient-chat/inbox");
            setInbox(data || []);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not load Patient Chat inbox");
        } finally {
            setInboxLoading(false);
        }
    }, []);

    /* iter98.36 — Fetch the full patient roster so the inbox search
       surfaces patients with NO prior thread (used to be limited to
       people the doctor had already chatted with, which made starting
       a new conversation a 3-click detour through My Patients). */
    const reloadRoster = useCallback(async () => {
        try {
            const { data } = await api.get("/doctor/patients");
            setRoster(data || []);
        } catch {
            /* non-critical — search falls back to inbox-only matches */
        }
    }, []);

    useEffect(() => { reloadInbox(); reloadRoster(); }, [reloadInbox, reloadRoster]);

    /* Bootstrap the templates list. If the clinic is brand-new and has
       no templates yet, seed the 6 defaults — done once on mount; the
       seed endpoint is idempotent so a re-mount is harmless. */
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                let r = await api.get("/doctor/chat-templates");
                if (!cancelled && (r.data || []).length === 0) {
                    await api.post("/doctor/chat-templates/seed-defaults").catch(() => null);
                    r = await api.get("/doctor/chat-templates");
                }
                if (!cancelled) setTemplates(r.data || []);
            } catch { /* non-critical — empty state still works */ }
        })();
        return () => { cancelled = true; };
    }, []);

    const reloadThread = useCallback(async (patientId, opts = {}) => {
        if (!patientId) { setThread([]); return; }
        if (!opts.silent) setThreadLoading(true);
        try {
            const { data } = await api.get(`/patients/${patientId}/messages`);
            setThread(data || []);
            if (!opts.skipMarkRead) {
                api.post(`/patients/${patientId}/messages/mark-read`)
                    .then((r) => { if (r.data?.marked > 0) reloadInbox(); })
                    .catch(() => {});
            }
        } catch (err) {
            if (!opts.silent) toast.error(err.response?.data?.detail || "Could not load thread");
        } finally {
            if (!opts.silent) setThreadLoading(false);
        }
    }, [reloadInbox]);

    /* Whenever the active patient changes — clear the draft + pending
       attachments, then load the new thread. */
    useEffect(() => {
        setPendingAttachments([]);
        setDraft("");
        reloadThread(activePatientId);
    }, [activePatientId, reloadThread]);

    /* 30s polling — pauses while tab is hidden, skips mark-read (only
       fired on user-initiated thread open). */
    useEffect(() => {
        if (!activePatientId) return undefined;
        let cancelled = false;
        const tick = async () => {
            if (cancelled || document.hidden) return;
            await reloadThread(activePatientId, { silent: true, skipMarkRead: true });
        };
        pollTimerRef.current = setInterval(tick, POLL_MS);
        return () => {
            cancelled = true;
            if (pollTimerRef.current) clearInterval(pollTimerRef.current);
        };
    }, [activePatientId, reloadThread]);

    useEffect(() => {
        scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
    }, [thread.length]);

    const onFilesPicked = useCallback(async (files) => {
        if (!activePatientId) return;
        const list = Array.from(files || []);
        if (list.length === 0) return;
        for (const file of list) {
            setUploadingCount((n) => n + 1);
            try {
                const form = new FormData();
                form.append("file", file);
                const { data } = await api.post(
                    `/patients/${activePatientId}/chat-upload`, form,
                    { headers: { "Content-Type": "multipart/form-data" } },
                );
                setPendingAttachments((arr) => [...arr, {
                    id: data.id,
                    name: data.name,
                    content_type: data.content_type,
                    size: data.size,
                    url: `/api/patient-chat-files/${data.id}`,
                    is_image: (data.content_type || "").startsWith("image/"),
                }]);
            } catch (err) {
                toast.error(err.response?.data?.detail || `Couldn't upload ${file.name}`);
            } finally {
                setUploadingCount((n) => n - 1);
            }
        }
        if (fileInputRef.current) fileInputRef.current.value = "";
    }, [activePatientId]);

    const removePendingAttachment = (id) => {
        setPendingAttachments((arr) => arr.filter((a) => a.id !== id));
    };

    const sendMessage = async () => {
        const text = draft.trim();
        const attachmentIds = pendingAttachments.map((a) => a.id);
        if ((!text && attachmentIds.length === 0) || sending || !activePatientId) return;
        setSending(true);
        const optimisticId = `opt-${Date.now()}`;
        const optimistic = {
            id: optimisticId, author_name: "You", author_role: "doctor",
            text, attachments: pendingAttachments, read_by: [],
            created_at: new Date().toISOString(), _pending: true,
        };
        setThread((m) => [...m, optimistic]);
        setDraft("");
        setPendingAttachments([]);
        try {
            const { data } = await api.post(`/patients/${activePatientId}/messages`, {
                text, attachment_file_ids: attachmentIds,
            });
            setThread((m) => m.map((x) => (x.id === optimisticId ? data : x)));
            reloadInbox();
            if (pollTimerRef.current) clearInterval(pollTimerRef.current);
            let bursts = 0;
            const fast = setInterval(() => {
                bursts += 1;
                if (bursts > 6) {
                    clearInterval(fast);
                    pollTimerRef.current = setInterval(
                        () => !document.hidden && reloadThread(activePatientId, { silent: true, skipMarkRead: true }),
                        POLL_MS,
                    );
                    return;
                }
                if (!document.hidden) reloadThread(activePatientId, { silent: true, skipMarkRead: true });
            }, FAST_POLL_MS);
            pollTimerRef.current = fast;
        } catch (err) {
            setThread((m) => m.filter((x) => x.id !== optimisticId));
            setDraft(text);
            setPendingAttachments(optimistic.attachments || []);
            toast.error(err.response?.data?.detail || "Couldn't send message");
        } finally {
            setSending(false);
        }
    };

    const filteredInbox = useMemo(() => {
        const q = inboxQ.trim().toLowerCase();
        if (!q) return inbox;
        // First: matches inside the existing-thread inbox.
        const inboxMatches = inbox.filter((p) =>
            (p.name || "").toLowerCase().includes(q) ||
            (p.phone || "").includes(inboxQ) ||
            (p.last_message_preview || "").toLowerCase().includes(q)
        );
        // Then: roster matches the doctor hasn't chatted with yet,
        // shown after the existing-thread matches with a "_noThread"
        // flag so InboxRow can render the "Start conversation" hint.
        const inboxIds = new Set(inbox.map((p) => p.id));
        const newCandidates = roster
            .filter((p) => !inboxIds.has(p.id))
            .filter((p) =>
                (p.name || "").toLowerCase().includes(q) ||
                (p.phone || "").includes(inboxQ)
            )
            .slice(0, 20)  // cap to keep the list scannable
            .map((p) => ({
                id: p.id,
                name: p.name,
                phone: p.phone,
                photo_data_url: p.photo_data_url,
                // iter98.55 — Propagate `has_case` into the picker rows
                // so the inbox can stamp a lock badge on case-less
                // patients without re-querying the roster.
                has_case: p.has_case,
                unread_count: 0,
                last_message_preview: null,
                last_message_at: null,
                _noThread: true,
            }));
        return [...inboxMatches, ...newCandidates];
    }, [inbox, inboxQ, roster]);

    const setActive = (patientId) => {
        const next = new URLSearchParams(searchParams);
        if (patientId) next.set("patient", patientId); else next.delete("patient");
        setSearchParams(next, { replace: true });
    };

    // iter98.36 — Roster fallback: if the active patient isn't in the
    // inbox yet (e.g. deep-linked via ?patient=<id> from Patient Profile
    // or My Patients menu, or just picked from the search dropdown
    // before any messages exist), pull their meta from the roster so
    // ThreadHeader still renders name + phone + avatar correctly
    // instead of the ugly "Patient · —" placeholder.
    const activePatientMeta =
        inbox.find((p) => p.id === activePatientId) ||
        roster.find((p) => p.id === activePatientId) ||
        null;

    /* iter98.55 — Lock the composer when the selected patient has no
       case on file at this clinic. We only lock on an explicit `false`
       so deep-links into a patient whose roster row hasn't loaded yet
       don't flash the lock state. The backend also enforces this with
       a 403, so the lock here is purely a UX hint. */
    const isLocked = activePatientMeta?.has_case === false;

    return (
        <div data-testid="doctor-patient-chat" className="space-y-4">
            {/* ── Page header ── matches /doctor/cases + /doctor/patients grammar */}
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div>
                    <h1 className="font-display text-2xl font-bold tracking-tight">Patient Chat</h1>
                    <p className="text-xs text-muted-foreground mt-1">
                        One thread per patient across every case at your clinic — quick check-ins, care tips, and follow-ups.
                    </p>
                </div>
            </div>
            <div className="h-[calc(100vh-13rem)] min-h-[500px] flex gap-4">
            {/* LEFT — patient inbox */}
            <aside className="w-[300px] shrink-0 bg-card border border-border rounded-2xl flex flex-col overflow-hidden">
                <div className="p-3 border-b border-border space-y-2.5">
                    <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-1.5">
                            <ChatCircleDots size={15} weight="duotone" className="text-primary" />
                            <h2 className="font-display text-sm font-bold">Inbox</h2>
                        </div>
                        <Button
                            type="button" variant="ghost" size="sm"
                            className="h-7 px-2 text-[11px]"
                            onClick={() => { reloadInbox(); if (activePatientId) reloadThread(activePatientId); }}
                            data-testid="patient-chat-refresh"
                        >
                            <ArrowsClockwise size={11} className="mr-1" /> Refresh
                        </Button>
                    </div>
                    <div className="relative">
                        <MagnifyingGlass size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <input
                            value={inboxQ}
                            onChange={(e) => setInboxQ(e.target.value)}
                            placeholder="Search patient name / phone…"
                            className="w-full h-8 pl-7 pr-2 text-xs rounded-md border border-border bg-background"
                            data-testid="patient-chat-inbox-search"
                        />
                    </div>
                </div>
                <div className="flex-1 overflow-y-auto" data-testid="patient-chat-inbox">
                    {inboxLoading ? (
                        <div className="p-3 space-y-3">
                            {[1, 2, 3].map((i) => (
                                <div key={i} className="space-y-1.5 animate-pulse">
                                    <div className="h-3 bg-muted rounded w-2/3" />
                                    <div className="h-2 bg-muted rounded w-1/2" />
                                </div>
                            ))}
                        </div>
                    ) : filteredInbox.length === 0 ? (
                        <InboxEmpty hasFilter={inboxQ.length > 0} />
                    ) : (
                        filteredInbox.map((p) => (
                            <InboxRow
                                key={p.id} row={p}
                                active={p.id === activePatientId}
                                onClick={() => setActive(p.id)}
                                locked={p.has_case === false}
                            />
                        ))
                    )}
                </div>
            </aside>

            {/* RIGHT — thread */}
            <div className="flex-1 min-w-0 bg-card border border-border rounded-2xl flex flex-col overflow-hidden">
                {!activePatientId ? (
                    <ThreadEmpty
                        hasInbox={inbox.length > 0}
                        recents={inbox.slice(0, 5)}
                        templates={templates}
                        onPickPatient={(pid) => setActive(pid)}
                        onPickTemplate={(text) => setDraft(text)}
                    />
                ) : (
                    <>
                        <ThreadHeader
                            meta={activePatientMeta}
                            patientId={activePatientId}
                            trayOpen={trayOpen}
                            onToggleTray={() => setTrayOpen((v) => !v)}
                        />
                        {trayOpen && templates.length > 0 && (
                            <div
                                className="border-b border-border bg-secondary/30 p-2.5 flex flex-wrap gap-1.5"
                                data-testid="patient-chat-template-tray"
                            >
                                {templates.map((t) => {
                                    const Icon = TEMPLATE_CATEGORY_ICON[t.category] || Note;
                                    return (
                                        <button
                                            key={t.id}
                                            type="button"
                                            onClick={() => { setDraft(t.body); setTrayOpen(false); }}
                                            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-border bg-card hover:bg-primary/10 hover:border-primary/40 hover:text-primary text-[11px] transition-colors"
                                            data-testid={`patient-chat-tray-${t.id}`}
                                        >
                                            <Icon size={12} weight="duotone" className="text-primary" />
                                            {t.title}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                        <div
                            ref={scrollRef}
                            className="flex-1 overflow-y-auto p-5 space-y-3"
                            data-testid="patient-chat-thread"
                        >
                            {threadLoading && thread.length === 0 ? (
                                <div className="text-center text-muted-foreground text-xs py-12">
                                    <Spinner size={20} className="mx-auto mb-2 animate-spin opacity-60" />
                                    Loading thread…
                                </div>
                            ) : thread.length === 0 ? (
                                <div className="text-center text-muted-foreground text-xs py-12">
                                    <ChatCircleDots size={22} weight="duotone" className="mx-auto mb-2 text-muted-foreground/60" />
                                    No messages yet — start the conversation.
                                </div>
                            ) : (
                                thread.map((m) => <MessageBubble key={m.id} msg={m} />)
                            )}
                        </div>
                        <Composer
                            draft={draft} setDraft={setDraft}
                            onSend={sendMessage} sending={sending}
                            pendingAttachments={pendingAttachments}
                            onFilesPicked={onFilesPicked}
                            removePendingAttachment={removePendingAttachment}
                            uploadingCount={uploadingCount}
                            fileInputRef={fileInputRef}
                            templates={templates}
                            locked={isLocked}
                            patientId={activePatientId}
                            patientName={activePatientMeta?.name}
                        />
                    </>
                )}
            </div>
        </div>
        </div>
    );
}


function InboxEmpty({ hasFilter }) {
    return (
        <div className="p-6 text-center">
            <ChatCircleDots size={26} weight="duotone" className="mx-auto mb-2 text-muted-foreground/60" />
            <p className="text-xs text-muted-foreground">
                {hasFilter ? "No matches in this view." : "No conversations yet."}
            </p>
            {!hasFilter && (
                <p className="text-[11px] text-muted-foreground/80 mt-2 leading-relaxed">
                    Type any patient's <span className="font-semibold text-foreground/80">name</span> or <span className="font-semibold text-foreground/80">phone</span> in the search above to start a new conversation.
                </p>
            )}
        </div>
    );
}


function InboxRow({ row, active, onClick, locked = false }) {
    const unread = row.unread_count || 0;
    const tint = avatarTintFor(row.name);
    return (
        <button
            type="button" onClick={onClick}
            className={`relative w-full text-left px-4 py-3 border-b border-border last:border-b-0 transition-colors ${
                active ? "bg-primary/10" : "hover:bg-secondary/50"
            }`}
            data-testid={`patient-chat-inbox-row-${row.id}`}
        >
            <div className="flex items-start gap-3">
                {row.photo_data_url ? (
                    <img
                        src={row.photo_data_url}
                        alt={row.name || "Patient"}
                        className={`w-9 h-9 rounded-full object-cover border ${tint.border} shrink-0`}
                    />
                ) : (
                    <div className={`w-9 h-9 rounded-full grid place-items-center text-[11px] font-semibold border ${tint.bg} ${tint.border} ${tint.text} shrink-0`}>
                        {initialsOf(row.name)}
                    </div>
                )}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                        <div className={`text-sm truncate flex items-center gap-1.5 ${unread > 0 ? "font-bold" : "font-semibold"} text-foreground`}>
                            <span className="truncate">{row.name || "Patient"}</span>
                            {/* iter98.55 — Lock icon in roster picker for case-less
                               patients. Doctor sees instantly which contacts can't
                               receive messages yet. */}
                            {locked && (
                                <Lock size={11} weight="duotone" className="text-muted-foreground shrink-0" data-testid={`patient-chat-inbox-row-${row.id}-locked`} />
                            )}
                        </div>
                        <div className="text-[10px] text-muted-foreground shrink-0 tabular-nums">
                            {fmtTime(row.last_message_at)}
                        </div>
                    </div>
                    <div className="text-[11px] text-muted-foreground truncate mt-0.5">
                        {row.phone || "—"}
                    </div>
                    <div className={`text-[12px] mt-1 truncate ${
                        unread > 0 ? "text-foreground font-medium" : "text-muted-foreground"
                    }`}>
                        {locked ? (
                            <span className="inline-flex items-center gap-1 text-amber-700/90 font-medium">
                                <Lock size={9} weight="bold" /> No active case — chat locked
                            </span>
                        ) : row._noThread ? (
                            <span className="inline-flex items-center gap-1 text-primary/90 font-medium">
                                <ArrowRight size={10} weight="bold" /> Start a new conversation
                            </span>
                        ) : (row.last_message_preview || "—")}
                    </div>
                </div>
            </div>
            {unread > 0 && (
                <span
                    className="absolute top-4 right-4 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full bg-primary text-white text-[9px] font-bold tabular-nums"
                    data-testid={`patient-chat-unread-${row.id}`}
                >
                    {unread > 99 ? "99+" : unread}
                </span>
            )}
        </button>
    );
}


function ThreadHeader({ meta, patientId, onToggleTray, trayOpen }) {
    const tint = avatarTintFor(meta?.name);
    return (
        <div className="px-4 py-3 border-b border-border flex items-center gap-3" data-testid="patient-chat-thread-header">
            {/* Patient avatar — photo when available, initials fallback */}
            {meta?.photo_data_url ? (
                <img
                    src={meta.photo_data_url}
                    alt={meta.name || "Patient"}
                    className={`w-9 h-9 rounded-full object-cover border ${tint.border} shrink-0`}
                />
            ) : (
                <div className={`w-9 h-9 rounded-full grid place-items-center text-[11px] font-semibold border shrink-0 ${tint.bg} ${tint.border} ${tint.text}`}>
                    {initialsOf(meta?.name)}
                </div>
            )}
            <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm truncate">{meta?.name || "Patient"}</div>
                <div className="text-[11px] text-muted-foreground truncate mt-0.5">{meta?.phone || "—"}</div>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
                <Link
                    to={`/doctor/patients/${patientId}`}
                    className="w-8 h-8 rounded-md border border-border bg-card hover:bg-secondary/60 grid place-items-center text-muted-foreground transition-colors"
                    title="Open patient profile"
                    data-testid="patient-chat-open-patient"
                >
                    <User size={14} />
                </Link>
                <Link
                    to={`/doctor/patients/${patientId}?tab=notes`}
                    className="w-8 h-8 rounded-md border border-border bg-card hover:bg-secondary/60 grid place-items-center text-muted-foreground transition-colors"
                    title="Patient notes"
                    data-testid="patient-chat-open-notes"
                >
                    <Notebook size={14} />
                </Link>
                <button
                    type="button"
                    onClick={onToggleTray}
                    className={`w-8 h-8 rounded-md border border-border grid place-items-center transition-colors ${
                        trayOpen ? "bg-primary text-white border-primary" : "bg-card hover:bg-secondary/60 text-muted-foreground"
                    }`}
                    title="Insert template"
                    data-testid="patient-chat-toggle-tray"
                >
                    <Sparkle size={14} weight={trayOpen ? "fill" : "regular"} />
                </button>
                <Link
                    to={`/doctor/patients/${patientId}`}
                    className="w-8 h-8 rounded-md border border-border bg-card hover:bg-secondary/60 grid place-items-center text-muted-foreground transition-colors"
                    title="More options"
                    data-testid="patient-chat-more-options"
                >
                    <DotsThreeVertical size={14} weight="bold" />
                </Link>
            </div>
        </div>
    );
}


function ThreadEmpty({ hasInbox, recents = [], templates = [], onPickPatient, onPickTemplate }) {
    return (
        <div className="flex-1 overflow-y-auto p-6 sm:p-8" data-testid="patient-chat-empty">
            <div className="max-w-lg mx-auto">
                {/* Hero — circular icon + title + sub */}
                <div className="text-center">
                    <div className="inline-flex w-14 h-14 rounded-full bg-secondary items-center justify-center mb-3">
                        <ChatCircleDots size={26} weight="duotone" className="text-muted-foreground" />
                    </div>
                    <h3 className="font-display text-xl font-bold">
                        {hasInbox ? "Pick a patient to start" : "No conversations yet"}
                    </h3>
                    <p className="text-xs text-muted-foreground mt-1.5">
                        {hasInbox
                            ? "One thread per patient — covers any cases under your care."
                            : "When you message a patient for the first time, they'll appear here."}
                    </p>
                </div>

                {/* Recent card — single most-recent patient (mirrors the
                    mockup's "RECENT" affordance). Saves a click vs the
                    left rail when bouncing between patients. */}
                {recents.length > 0 && (
                    <div className="mt-6" data-testid="patient-chat-recent-patients">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-2">Recent</div>
                        <button
                            type="button"
                            onClick={() => onPickPatient?.(recents[0].id)}
                            className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl border border-border bg-card hover:bg-secondary/50 hover:border-primary/40 transition text-left"
                            data-testid={`patient-chat-recent-${recents[0].id}`}
                        >
                            <div className="flex items-center gap-3 min-w-0">
                                {recents[0].photo_data_url ? (
                                    <img
                                        src={recents[0].photo_data_url}
                                        alt={recents[0].name || "Patient"}
                                        className="w-9 h-9 rounded-full object-cover border border-border shrink-0"
                                    />
                                ) : (
                                    (() => {
                                        const t = avatarTintFor(recents[0].name);
                                        return (
                                            <div className={`w-9 h-9 rounded-full grid place-items-center text-[11px] font-semibold border ${t.bg} ${t.border} ${t.text} shrink-0`}>
                                                {initialsOf(recents[0].name)}
                                            </div>
                                        );
                                    })()
                                )}
                                <div className="min-w-0">
                                    <div className="font-semibold text-sm truncate">{recents[0].name || "Patient"}</div>
                                    <div className="text-[11px] text-muted-foreground truncate mt-0.5">
                                        {(recents[0].last_message_preview || "—").slice(0, 72)}
                                    </div>
                                </div>
                            </div>
                            <CaretRight size={14} className="text-muted-foreground shrink-0" />
                        </button>
                    </div>
                )}

                {/* Template grid — 6 canned messages laid out 2-up. The
                    mockup shows icons + 2-line previews. Clicking
                    pre-fills the draft; the doctor then picks a patient
                    on the left to send. */}
                {templates.length > 0 && (
                    <div className="mt-6" data-testid="patient-chat-templates">
                        <div className="flex items-center justify-between mb-2">
                            <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Templates</div>
                            <Link
                                to="/doctor/profile?tab=templates"
                                className="text-[11px] font-semibold text-primary hover:underline"
                                data-testid="patient-chat-templates-manage"
                            >
                                Manage
                            </Link>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {templates.map((t) => {
                                const Icon = TEMPLATE_CATEGORY_ICON[t.category] || Note;
                                return (
                                    <button
                                        key={t.id}
                                        type="button"
                                        onClick={() => onPickTemplate?.(t.body)}
                                        className="text-left rounded-xl border border-border bg-card hover:border-primary/40 hover:bg-secondary/40 transition p-3.5"
                                        data-testid={`patient-chat-template-${t.id}`}
                                    >
                                        <div className="font-semibold text-xs mb-1.5 inline-flex items-center gap-1.5">
                                            <Icon size={13} weight="duotone" className="text-primary" />
                                            {t.title}
                                        </div>
                                        <div className="text-[11px] text-muted-foreground line-clamp-2 leading-relaxed">{t.body}</div>
                                    </button>
                                );
                            })}
                        </div>
                        <p className="text-[10px] text-muted-foreground mt-3 text-center">
                            Tap a template to pre-fill the message. Then pick a patient on the left.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}


/* Bubble + attachments + read receipts — identical pattern to Lab Chat,
   only the "mine" rule changes (patient vs clinic instead of doctor vs
   COE). Kept inline so the file is self-contained. */
function MessageBubble({ msg }) {
    // Clinic side = doctor + clinic_staff. Patient side = patient. Either
    // way "mine" = the current viewer; in this file we're rendered for
    // the doctor, so doctor/clinic_staff bubbles are "mine".
    const mine = msg.author_role === "doctor" || msg.author_role === "clinic_staff";
    const meta = (() => {
        if (msg.author_role === "patient") return { label: msg.author_name, tone: "bg-emerald-50 text-emerald-800 border-emerald-200" };
        if (msg.author_role === "clinic_staff") return { label: `${msg.author_name} · Clinic`, tone: "bg-primary/10 text-primary border-primary/30" };
        return { label: msg.author_name, tone: "bg-primary/10 text-primary border-primary/30" };
    })();
    const attachments = msg.attachments || [];
    const recipientReads = (msg.read_by || []).filter((r) => r.user_id !== msg.author_id).length;
    const showCheck = mine && !msg._pending;
    return (
        <div className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`patient-chat-msg-${msg.id}`}>
            <div className="max-w-[78%] min-w-0">
                {!mine && (
                    <div className={`inline-block text-[10px] px-1.5 py-0.5 rounded font-semibold border mb-1 ${meta.tone}`}>
                        {meta.label}
                    </div>
                )}
                <div className={`rounded-2xl px-3.5 py-2 text-sm break-words shadow-sm ${
                    mine ? "bg-primary text-white rounded-br-md" : "bg-secondary/70 text-foreground rounded-bl-md"
                } ${msg._pending ? "opacity-60" : ""}`}>
                    {attachments.length > 0 && (
                        <div className={`space-y-1.5 ${msg.text ? "mb-2" : ""}`}>
                            {attachments.map((a) => <AttachmentChip key={a.id} att={a} mine={mine} />)}
                        </div>
                    )}
                    {msg.text && <div className="whitespace-pre-wrap">{msg.text}</div>}
                </div>
                <div className={`text-[10px] mt-1 flex items-center gap-1 ${mine ? "justify-end text-muted-foreground" : "text-muted-foreground"}`}>
                    <span>{fmtTime(msg.created_at)}{msg._pending && " · sending…"}</span>
                    {showCheck && (
                        recipientReads > 0 ? (
                            <Checks size={12} weight="bold" className="text-primary" title="Read by the patient" />
                        ) : (
                            <Check size={12} weight="bold" className="text-muted-foreground/70" title="Sent — not yet read" />
                        )
                    )}
                </div>
            </div>
        </div>
    );
}


/* AttachmentChip — image preview (lazy-loaded via auth-bearing blob
   fetch) or downloadable file card. Same pattern as Lab Chat but uses
   the /patient-chat-files/ endpoint. */
function AttachmentChip({ att, mine }) {
    const isImage = (att.content_type || "").startsWith("image/")
        || /\.(png|jpg|jpeg|gif|webp|heic)$/i.test(att.name || "");
    const [blobUrl, setBlobUrl] = useState(null);
    useEffect(() => {
        if (!isImage || !att.id) return undefined;
        let revoked = false;
        let url = null;
        api.get(`/patient-chat-files/${att.id}`, { responseType: "blob" })
            .then((r) => {
                if (revoked) return;
                url = URL.createObjectURL(r.data);
                setBlobUrl(url);
            })
            .catch(() => {});
        return () => {
            revoked = true;
            if (url) URL.revokeObjectURL(url);
        };
    }, [att.id, isImage]);

    const downloadFile = async () => {
        try {
            const r = await api.get(`/patient-chat-files/${att.id}`, { responseType: "blob" });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url; a.download = att.name || "download";
            document.body.appendChild(a); a.click(); a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 1500);
        } catch {
            toast.error("Couldn't download attachment");
        }
    };

    if (isImage) {
        return (
            <button type="button" onClick={downloadFile}
                className="block max-w-[260px] rounded-lg overflow-hidden border border-black/10 bg-black/5 cursor-zoom-in"
                data-testid={`patient-chat-attachment-${att.id}`} title={att.name}
            >
                {blobUrl ? (
                    <img src={blobUrl} alt={att.name} className="block max-h-[200px] w-auto" />
                ) : (
                    <div className="w-[200px] h-[120px] flex items-center justify-center text-xs text-muted-foreground">
                        <Spinner size={16} className="animate-spin" />
                    </div>
                )}
            </button>
        );
    }
    const cardCls = mine
        ? "bg-white/10 hover:bg-white/20 border-white/20 text-white"
        : "bg-card hover:bg-secondary border-border text-foreground";
    return (
        <button type="button" onClick={downloadFile}
            className={`flex items-center gap-2 max-w-[260px] px-2.5 py-2 rounded-lg border text-left ${cardCls}`}
            data-testid={`patient-chat-attachment-${att.id}`}
        >
            <FileText size={18} weight="duotone" className="shrink-0" />
            <div className="min-w-0 flex-1">
                <div className="text-xs font-semibold truncate">{att.name || "File"}</div>
                <div className={`text-[10px] ${mine ? "text-white/70" : "text-muted-foreground"}`}>{fmtSize(att.size)}</div>
            </div>
        </button>
    );
}


function LockedComposer({ patientId, patientName }) {
    /* iter98.55 — Shown in place of the message Composer when the
       selected patient has no case on file. Mirrors the patient's own
       "Messaging unlocks after your first visit" empty state so both
       sides of the relationship explain the same rule. The "Create
       case" CTA jumps to the wizard with the patient pre-selected so
       the doctor isn't bounced through My Patients first. */
    const firstName = (patientName || "this patient").split(" ")[0];
    return (
        <div
            className="border-t border-border p-5 bg-amber-50/40"
            data-testid="patient-chat-locked-composer"
        >
            <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-full bg-amber-100 grid place-items-center shrink-0">
                    <Lock size={16} weight="duotone" className="text-amber-700" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="font-display text-sm font-bold text-foreground">
                        Chat unlocks after {firstName}'s first case
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-1 leading-relaxed">
                        We mirror this lock on the patient side too — they see
                        <span className="italic"> "Messaging unlocks after your first visit"</span>.
                        Submit a case to start the conversation; the thread becomes available the moment the case is created.
                    </p>
                    <div className="flex items-center gap-2 mt-3">
                        <Link
                            to={`/doctor/cases/new?patient_id=${patientId || ""}`}
                            className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider bg-primary text-white rounded-full px-3.5 py-1.5 hover:opacity-90 transition-opacity"
                            data-testid="patient-chat-locked-create-case"
                        >
                            <Plus size={11} weight="bold" /> Create case
                        </Link>
                        <Link
                            to={`/doctor/patients/${patientId || ""}`}
                            className="text-[11px] font-semibold text-primary hover:underline"
                            data-testid="patient-chat-locked-open-profile"
                        >
                            Open profile →
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
}


function Composer({
    draft, setDraft, onSend, sending,
    pendingAttachments = [], onFilesPicked,
    removePendingAttachment, uploadingCount = 0, fileInputRef,
    templates = [],
    /* iter98.55 — When `locked` is true, the whole composer renders as
       a single read-only banner pointing the doctor at the "Create
       case" CTA instead of accepting input the backend will 403. */
    locked = false,
    patientId,
    patientName,
}) {
    if (locked) {
        return <LockedComposer patientId={patientId} patientName={patientName} />;
    }
    const onKey = (e) => {
        if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
            e.preventDefault();
            onSend();
        }
    };
    const canSend = !sending && uploadingCount === 0 && (draft.trim().length > 0 || pendingAttachments.length > 0);
    return (
        <div className="border-t border-border p-3 bg-background/50">
            {(pendingAttachments.length > 0 || uploadingCount > 0) && (
                <div className="mb-2 flex flex-wrap gap-2" data-testid="patient-chat-attachments-tray">
                    {pendingAttachments.map((a) => (
                        <div key={a.id} className="relative inline-flex items-center gap-1.5 pl-2 pr-1 py-1 rounded-md bg-secondary border border-border text-[11px]">
                            {a.is_image
                                ? <ImageIcon size={13} weight="duotone" className="text-primary shrink-0" />
                                : <FileText size={13} weight="duotone" className="text-muted-foreground shrink-0" />}
                            <span className="font-medium truncate max-w-[140px]">{a.name}</span>
                            <button type="button" onClick={() => removePendingAttachment(a.id)}
                                className="w-5 h-5 rounded-full hover:bg-rose-100 hover:text-rose-700 flex items-center justify-center transition-colors"
                                title="Remove"
                            >
                                <X size={11} weight="bold" />
                            </button>
                        </div>
                    ))}
                    {uploadingCount > 0 && (
                        <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-secondary/70 border border-border text-[11px] text-muted-foreground">
                            <Spinner size={12} className="animate-spin" />
                            Uploading {uploadingCount} file{uploadingCount === 1 ? "" : "s"}…
                        </div>
                    )}
                </div>
            )}
            <div className="flex items-end gap-2">
                <input
                    type="file" ref={fileInputRef}
                    onChange={(e) => onFilesPicked(e.target.files)}
                    className="hidden" multiple
                    accept="image/*,application/pdf"
                    data-testid="patient-chat-file-input"
                />
                <Button type="button" onClick={() => fileInputRef.current?.click()}
                    variant="outline" size="sm" className="h-10 px-2.5 shrink-0"
                    title="Attach files" data-testid="patient-chat-attach-btn"
                >
                    <Paperclip size={15} />
                </Button>
                <TemplateMenu templates={templates} onPick={(body) => setDraft(body)} />
                <Textarea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={onKey}
                    placeholder="Type your message to the patient…"
                    rows={2} className="resize-none text-sm"
                    data-testid="patient-chat-composer"
                />
                <Button type="button" onClick={onSend} disabled={!canSend}
                    size="sm" className="h-10 shrink-0" data-testid="patient-chat-send"
                >
                    {sending ? <Spinner size={14} className="animate-spin" /> : <PaperPlaneTilt size={14} weight="fill" />}
                    <span className="ml-1.5 hidden sm:inline">Send</span>
                </Button>
            </div>
            <div className="text-[10px] text-muted-foreground mt-1.5">
                Press Cmd / Ctrl + Enter to send. Attach images or PDFs.
            </div>
        </div>
    );
}


/* Templates dropdown — opens a popover with the canned messages
   fetched from /api/doctor/chat-templates. Used in the Composer so
   doctors don't have to retype the same compliance check-in /
   welcome message every time. Click outside closes. */
function TemplateMenu({ templates = [], onPick }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
        if (!open) return;
        const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", close);
        return () => document.removeEventListener("mousedown", close);
    }, [open]);
    if (templates.length === 0) return null;
    return (
        <div className="relative" ref={ref}>
            <Button
                type="button" onClick={() => setOpen((v) => !v)}
                variant="outline" size="sm" className="h-10 px-2.5 shrink-0"
                title="Insert message template"
                data-testid="patient-chat-template-btn"
            >
                <Sparkle size={15} weight="duotone" />
            </Button>
            {open && (
                <div
                    className="absolute bottom-12 left-0 w-80 bg-card border border-border rounded-xl shadow-xl z-50 overflow-hidden"
                    data-testid="patient-chat-template-menu"
                >
                    <div className="px-3 py-2 border-b border-border text-[10px] font-bold uppercase tracking-wider text-muted-foreground bg-secondary/40 flex items-center justify-between">
                        <span>Insert template</span>
                        <Link
                            to="/doctor/profile?tab=templates"
                            className="text-primary normal-case tracking-normal font-semibold"
                            onClick={() => setOpen(false)}
                            data-testid="patient-chat-template-menu-manage"
                        >
                            Manage →
                        </Link>
                    </div>
                    <ul className="max-h-72 overflow-y-auto">
                        {templates.map((t) => {
                            const Icon = TEMPLATE_CATEGORY_ICON[t.category] || Note;
                            return (
                                <li key={t.id}>
                                    <button
                                        type="button"
                                        onClick={() => { onPick?.(t.body); setOpen(false); }}
                                        className="w-full text-left px-3 py-2.5 hover:bg-secondary/40 border-b border-border last:border-b-0 flex items-start gap-2"
                                        data-testid={`patient-chat-template-menu-${t.id}`}
                                    >
                                        <Icon size={13} weight="duotone" className="text-primary shrink-0 mt-0.5" />
                                        <div className="min-w-0">
                                            <div className="font-semibold text-xs">{t.title}</div>
                                            <div className="text-[11px] text-muted-foreground line-clamp-1 mt-0.5">{t.body}</div>
                                        </div>
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}
        </div>
    );
}
